import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IonContent, IonIcon, IonSpinner } from "@ionic/angular/standalone";
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgxChartsModule, ScaleType } from '@swimlane/ngx-charts';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { PieChart, SunburstChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { map, Subject, takeUntil, tap } from 'rxjs';
import { addIcons } from 'ionicons';
import { filter, chevronDownOutline, refreshOutline, ellipsisVertical, checkmark, barChartOutline, wifi, alertOutline, timerOutline } from 'ionicons/icons';

// Services & Models
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { CustomerService } from '../../../../core/services/customer.service';
import { FacilitiesService } from '../../../../core/services/facilities.service';
import { RequestsService } from '../../../../core/services/utils/requests.service';
import { ToastService } from '../../../../core/services/utils/toast.service';
import { AnalyticsContext, DashboardWidget } from '../../../../shared/models/analytics';
import { IUser } from '../../../../shared/models/be/user';
import { EChartsOption } from 'echarts/types/dist/shared';
import { PostureStatisticsComponent } from '../../../../shared/components/posture-statistics/posture-statistics.component';
import { SensorConnectivityComponent } from '../../../../shared/components/sensor-connectivity/sensor-connectivity.component';
import { GuideService } from '../../../../core/services/guide.service';
import { AnalyticsTutorialComponent } from '../../../../shared/components/analytics-tutorial/analytics-tutorial.component';

echarts.use([PieChart, SunburstChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer]);

interface AlertTypeDatum {
  name?: unknown;
  value?: unknown;
}

export interface MobileAlertTypeDatum {
  name: string;
  value: number;
}

const MOBILE_ALERT_TYPES = [
  { key: 'room_in', name: 'Ingresso Stanza', aliases: ['02', 'codice 02', 'room in', 'entrata stanza', 'ingresso stanza'] },
  { key: 'room_out', name: 'Uscita Stanza', aliases: ['03', 'codice 03', 'room out', 'uscita stanza'] },
  { key: 'bed_in', name: 'Ingresso Letto', aliases: ['04', 'codice 04', 'bed in', 'a letto', 'entrata letto', 'ingresso letto'] },
  { key: 'bed_out', name: 'Uscita Letto', aliases: ['05', 'codice 05', 'bed out', 'uscita letto'] },
  { key: 'fall', name: 'Caduta', aliases: ['01', 'codice 01', 'fall', 'caduta'] }
] as const;

function normalizeAlertTypeName(name: unknown): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function filterMobileAlertTypes(data: readonly AlertTypeDatum[] | null | undefined): MobileAlertTypeDatum[] {
  const totals = new Map<string, number>();

  for (const item of data ?? []) {
    const normalizedName = normalizeAlertTypeName(item.name);
    const alertType = MOBILE_ALERT_TYPES.find(type => type.aliases.some(alias => alias === normalizedName));
    const value = Number(item.value);

    if (!alertType || !Number.isFinite(value) || value <= 0) continue;
    totals.set(alertType.key, (totals.get(alertType.key) ?? 0) + value);
  }

  return MOBILE_ALERT_TYPES.flatMap(type => {
    const value = totals.get(type.key);
    return value ? [{ name: type.name, value }] : [];
  });
}

@Component({
  selector: 'app-analytics-mobile',
  templateUrl: './analytics.mobile.component.html',
  styleUrls: ['./analytics.mobile.component.scss'],
  standalone: true,
  imports: [
    AnalyticsTutorialComponent,
    CommonModule, IonContent, IonIcon, IonSpinner,
    TranslateModule, FormsModule,
    MatExpansionModule, MatFormFieldModule, MatSelectModule, MatMenuModule, MatButtonModule, MatProgressBarModule,
    NgxChartsModule, NgxEchartsDirective, PostureStatisticsComponent, SensorConnectivityComponent
  ],
  providers: [provideEchartsCore({ echarts })]
})
export class AnalyticsMobilePage implements OnInit, OnDestroy {
  public readonly analyticsTutorialMode: boolean;

  public kpiWidgets: Omit<DashboardWidget, 'cols' | 'rows' | 'pinned'>[] = [];
  public chartWidgets: Omit<DashboardWidget, 'cols' | 'rows' | 'pinned'>[] = [];

  public isDataLoading: boolean = false;
  public sensorsDetailsOpen = false;
  private sensorCountRequestId = 0;
  private destroy$ = new Subject<void>();

  // Contesto & Utente
  public currentUser: IUser | null = null;
  public currentContext: AnalyticsContext = { customerId: undefined, organizationId: undefined, buildingId: undefined, unitId: undefined };
  public isContextLoading: boolean = false;

  // Liste Select
  public customersList: any[] = [];
  public organizationsList: any[] = [];
  public buildingsList: any[] = [];
  public unitsList: any[] = [];

  public filterMap: Record<string, string> = {
    '24h': '24 Ore', '7d': '7 Giorni', '30d': '30 Giorni',
    'all_alerts': 'Tutte', 'managed_alerts': 'Gestite', 'unmanaged_alerts': 'Non Gestite',
  };

  constructor(
    private analyticsService: AnalyticsService,
    private customerService: CustomerService,
    private facilitiesService: FacilitiesService,
    private requestsService: RequestsService,
    private toastService: ToastService,
    public guide: GuideService = null!
  ) {
    this.analyticsTutorialMode = guide?.analyticsPracticeRequested() ?? false;
    addIcons({ filter, chevronDownOutline, refreshOutline, ellipsisVertical, checkmark, barChartOutline, wifi, alertOutline, timerOutline });
  }

  ngOnInit() {
    if (this.analyticsTutorialMode) return;
    this.currentUser = this.requestsService.getCurrentUser();
    this.setupInitialContext();
    this.initStaticWidgets(); // Definisco i widget hardcoded per mobile
  }

  // Definisco i widget MANUALMENTE per il mobile (niente caricamento dinamico da DB per il layout)
  private initStaticWidgets() {
    // 1. KPI (Griglia in alto)
    this.kpiWidgets = [
      {
        id: 'kpi_sensors', widgetType: 'simple_on', label: 'Sensori operativi', icon: 'wifi',
        section: 'sensors', visible: true, loading: true, isGlobalOnly: false,
        dataLoader: (f, c) => this.analyticsService.getKpiSensors$(c), data: null
      },
      {
        id: 'kpi_alerts', widgetType: 'simple', label: 'Allerte (24h)', icon: 'alert-outline',
        section: 'alerts', visible: true, loading: true, currentFilter: '24h', isGlobalOnly: false,
        dataLoader: (f, c) => this.analyticsService.getKpiAlerts$(f, c), data: null
      },
      {
        id: 'kpi_response', widgetType: 'simple', label: 'Risposta (Media)', icon: 'timer-outline',
        section: 'alerts', visible: true, loading: true, currentFilter: '24h', isGlobalOnly: false,
        dataLoader: (f, c) => this.analyticsService.getKpiResponseTime$(f, c), data: null
      }
    ];

    // 2. Grafici (Lista verticale)
    this.chartWidgets = [
      {
        id: 'chart_weekly', widgetType: 'vertical_bars_chart', label: 'Andamento Settimanale',
        section: 'alerts', visible: true, loading: true, currentFilter: '7d', isGlobalOnly: false,
        chartScheme: { domain: ['#00AEBE'] } as any,
        dataLoader: (f, c) => this.analyticsService.getWeeklyAlerts$(f, c), data: null
      },
      {
        id: 'alert_types', widgetType: 'pie_chart', label: 'Tipologia Allerte',
        section: 'alerts', visible: true, loading: true, currentFilter: '7d', availableFilters: ['24h', '7d', '30d'], isGlobalOnly: false,
        dataLoader: (f, c) => this.analyticsService.getAlertTypes$(f, c).pipe(
          map(data => filterMobileAlertTypes(data))
        ),
        data: null
      }
    ];

    if (this.isSuperUser) {
      this.chartWidgets.push({
        id: 'kpi_cost', widgetType: 'advanced_pie_chart', label: 'Costi Azure',
        section: 'costs', visible: true, loading: true, currentFilter: '30d', isGlobalOnly: true,
        dataLoader: (f) => this.analyticsService.getKpiCost$(f), data: null
      });
    }

    // Caricamento iniziale Dati
    this.loadAllData();
  }

  private setupInitialContext() {
    if (this.isSuperUser) {
      this.loadCustomers();
    }
    if (this.currentUser?.customer?.id) {
      this.currentContext.customerId = this.currentUser.customer.id;
      this.onContextChange('customer');
    }
    if (this.currentUser?.organization?.id) {
      this.currentContext.organizationId = this.currentUser.organization.id;
      this.onContextChange('organization');
    }
  }

  loadAllData() {
    [...this.kpiWidgets, ...this.chartWidgets].forEach(w => this.loadWidgetData(w as any));
  }

  // 0. Caricamento iniziale
  loadCustomers() {
    this.customerService.getCustomers$().pipe(
      takeUntil(this.destroy$),
      tap((res) => {
        this.customersList = res;
      })
    ).subscribe()
  }

  loadWidgetData(widget: DashboardWidget) {
    const sensorRequestId = widget.id === 'kpi_sensors' ? ++this.sensorCountRequestId : undefined;
    if (sensorRequestId !== undefined) widget.data = undefined;
    widget.loading = true;
    const context = widget.isGlobalOnly ? {} : this.currentContext;
    widget.dataLoader(widget.currentFilter, context).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (sensorRequestId !== undefined && sensorRequestId !== this.sensorCountRequestId) return;
        widget.data = data; widget.loading = false;
      },
      error: () => {
        if (sensorRequestId !== undefined && sensorRequestId !== this.sensorCountRequestId) return;
        widget.loading = false;
      }
    });
  }

  // --- FILTRI ---
  onContextChange(level: 'customer' | 'organization' | 'building' | 'unit') {
    this.isContextLoading = true;

    // Logica a cascata: Se cambia il padre, resetto i figli
    switch (level) {
      case 'customer':
        this.currentContext.organizationId = undefined;
        this.currentContext.buildingId = undefined;
        this.currentContext.unitId = undefined;
        this.organizationsList = [];
        this.buildingsList = [];
        this.unitsList = [];

        if (this.currentContext.customerId) {
          // TODO: Carica Organizzazioni del cliente
          this.facilitiesService.getOrganization$(this.currentContext.customerId).pipe(
            takeUntil(this.destroy$),
            tap((res) => {
              this.organizationsList = res;
              this.isContextLoading = false;
            })
          ).subscribe()
        } else {
          this.isContextLoading = false;
        }
        break;

      case 'organization':
        this.currentContext.buildingId = undefined;
        this.currentContext.unitId = undefined;
        this.buildingsList = [];
        this.unitsList = [];

        if (this.currentContext.organizationId) {
          this.facilitiesService.getBuildings$(this.currentContext.organizationId).pipe(
            takeUntil(this.destroy$),
            tap((res) => {
              this.buildingsList = res;
              this.isContextLoading = false;
            })
          ).subscribe()
        } else {
          this.isContextLoading = false;
        }
        break;

      case 'building':
        this.currentContext.unitId = undefined;
        this.unitsList = [];

        if (this.currentContext.buildingId) {
          this.facilitiesService.getUnits$(this.currentContext.buildingId).pipe(
            takeUntil(this.destroy$),
            tap((res) => {
              this.unitsList = res;
              this.isContextLoading = false;
            })
          ).subscribe()
        } else {
          this.isContextLoading = false;
        }
        break;

      case 'unit':
        this.isContextLoading = false;
        break;
    }
    this.reloadContextAwareWidgets();
  }

  reloadContextAwareWidgets() {
    [...this.kpiWidgets, ...this.chartWidgets].filter(w => !w.isGlobalOnly).forEach(w => this.loadWidgetData(w as any));
    this.isContextLoading = false;
  }

  resetContext() {
    // Logica di reset (copia dal WebApp)
    this.reloadContextAwareWidgets();
  }

  onFilterChange(widget: DashboardWidget, filter: string) {
    widget.currentFilter = filter;
    this.loadWidgetData(widget);
  }

  // --- ECHARTS OPTIONS (Semplificate per Mobile) ---
  getEchartsPieOptions(widget: DashboardWidget): EChartsOption {
    const data = widget.data || [];

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        textStyle: { color: '#333' },
        confine: true // Assicura che il tooltip non esca dallo schermo su mobile
      },

      legend: {
        // MODIFICA 1: Legenda completa
        type: 'plain', // 'plain' mostra tutto e va a capo, 'scroll' metteva le frecce
        bottom: 0,
        left: 'center',
        width: '100%',
        itemWidth: 10,
        itemHeight: 10,
        icon: 'circle',
        orient: 'horizontal', // Si disporrà orizzontalmente andando a capo

        textStyle: {
          fontSize: 11,
          color: '#344767',
          padding: [0, 5, 0, 0]
        },

        formatter: (name: string) => {
          const item = data.find((d: any) => d.name === name);
          return item ? `${name}: ${item.value}` : name;
        }
      },

      series: [{
        type: 'pie',
        radius: ['45%', '60%'], // Leggermente ridotto per lasciare spazio alla legenda che ora potrebbe essere più alta
        center: ['50%', '35%'], // Spostato più in alto per non sovrapporsi alla legenda "full"

        // MODIFICA 2: Disabilita interazione click
        selectedMode: false, // Impedisce alla fetta di staccarsi quando cliccata
        emphasis: {
          scale: false, // Impedisce alla fetta di ingrandirsi quando toccata
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },

        data: data,
        label: { show: false },
        labelLine: { show: false },

        itemStyle: {
          borderRadius: 5,
          borderColor: '#f5f7fa',
          borderWidth: 3
        }
      }]
    };
  }

  getSunburstOptions(widget: DashboardWidget): EChartsOption {
    // Usa la stessa logica del web ma rimuovi label in eccesso
    return {
      series: {
        type: 'sunburst', data: widget.data || [], radius: ['10%', '90%'],
        label: { show: false }, // Fondamentale su mobile
        levels: [{}, { itemStyle: { color: '#00AEBE' } }, { itemStyle: { color: '#91cc75' } }]
      }
    } as any;
  }

  get isSuperUser() { return this.currentUser?.role === 'superuser'; }
  get hasActiveFilters() { return this.isSuperUser ? !!this.currentContext.customerId : !!this.currentContext.organizationId; }

  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
